import { AxiosError, AxiosResponse } from 'axios';
import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorInfo, TaskSearchConditions, TaskSearchResult } from '../../../api/model';
import { usePostTasksDownload } from '../../../api/tasks/tasks';
import { useOptionsForBinary } from '../../../hooks/api';
import { executeDownload } from '../../../utils/download';
import ErrorComponent from '../../atoms/ErrorComponent';
import WaitComponent from '../../atoms/WaitComponent';
import BlobErrorModal from '../../modules/BlobErrorModal';
import OkModal from '../../modules/OkModal';
import SpinnerModal from '../../modules/SpinnerModal';
import { TaskTable } from './TaskTable';

interface Props {
  data: AxiosResponse<TaskSearchResult, unknown> | undefined;
  isLoading: boolean;
  isError: boolean;
  error: AxiosError<ErrorInfo> | null;
  displayGroup: boolean;
  setDisplayGroup: (displayGroup: boolean) => void;
  conditionsRef: MutableRefObject<TaskSearchConditions>;
  setConditionsAndSearch: (value: TaskSearchConditions) => void;
}

function TaskList({
  data,
  isLoading,
  isError,
  error,
  displayGroup,
  setDisplayGroup,
  conditionsRef,
  setConditionsAndSearch,
}: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [currentTaskStatus, setCurrentTaskStatus] = useState(1);

  const [displayError, setDisplayError] = useState(false);

  const currentTasks = useMemo(
    () => (data?.data.tasks ? data?.data.tasks.filter((task) => task.task_status === 1) : []),
    [data?.data.tasks]
  );
  const completedTasks = useMemo(
    () => (data?.data.tasks ? data?.data.tasks.filter((task) => task.task_status === 2) : []),
    [data?.data.tasks]
  );
  const filteredTasks = useMemo(
    () => (data?.data.tasks ? data?.data.tasks.filter((task) => task.task_status === currentTaskStatus) : []),
    [currentTaskStatus, data?.data.tasks]
  );

  /**
   * 表示グループ切り替え時
   */
  const postTaskSearch = useCallback(
    (selectGroup: boolean) => {
      console.log(`★手動更新ON  ${new Date().toLocaleTimeString()}`);
      setDisplayGroup(selectGroup);
      const next: TaskSearchConditions = {
        ...conditionsRef.current,
        display_group: selectGroup,
      };
      setConditionsAndSearch(next);
    },
    [conditionsRef, setConditionsAndSearch, setDisplayGroup]
  );

  // #region ダウンロード
  // 一覧ダウンロード API
  const optionsForBinary = useOptionsForBinary(language);
  const {
    mutateAsync: downloadAsync,
    error: downloadError,
    isError: isDownloadError,
    isLoading: downloading,
  } = usePostTasksDownload<AxiosError<Blob>>(optionsForBinary);

  // ダウンロードエラーの表示
  const [displayDownloadError, setDisplayDownloadError] = useState(false);
  useEffect(() => {
    setDisplayDownloadError(isDownloadError);
  }, [isDownloadError]);

  // 一覧出力ボタン押下時
  const onClickDownload = useCallback(async () => {
    // ダウンロード API へのアクセス
    await downloadAsync(
      { data: { currenttasks: currentTasks, completedtasks: completedTasks } },
      {
        // API アクセスが成功したら、ダウンロードを実行
        onSuccess: (response) => executeDownload(response),
      }
    );
  }, [downloadAsync, currentTasks, completedTasks]);
  // #endregion

  // 待ち状態
  if (isLoading) {
    return <WaitComponent />;
  }

  // エラー表示
  if (isError && error?.response?.data.error_code !== 'CommentsNoPrivilege') {
    return <ErrorComponent error={error} />;
  }

  return (
    <div className="tab-area">
      <input id="tab-btn1" className="tab-btn" name="tab" type="radio" />
      <input id="tab-btn2" className="tab-btn" name="tab" type="radio" />
      <ul className="tab-list-wrap">
        <li>
          <label
            id="tab-list1"
            className={`tab-list ${currentTaskStatus === 1 ? 'current' : ''}`}
            htmlFor="tab-btn1"
            onClick={() => setCurrentTaskStatus(1)}
          >
            {t('タスク')}({currentTasks.length})
          </label>
        </li>
        <li>
          <label
            id="tab-list2"
            className={`tab-list ${currentTaskStatus === 2 ? 'current' : ''}`}
            htmlFor="tab-btn2"
            onClick={() => setCurrentTaskStatus(2)}
          >
            {t('完了')}({completedTasks.length})
          </label>
        </li>
      </ul>
      <div className="displayfr">
        <ul>
          <li className={`${displayGroup ? '' : 'current'}`}>
            <a href="#user" onClick={() => postTaskSearch(false)}>
              {' '}
              <i className="fa-solid fa-user " />
              {t('Display(User)')}
            </a>
          </li>
          <li className={`${displayGroup ? 'current' : ''}`}>
            <a href="#group" onClick={() => postTaskSearch(true)}>
              <i className="fa-solid fa-users" />
              {t('Display(Group)')}
            </a>
          </li>
          <li className={`${displayGroup ? 'current' : ''}`}>
            <a href="#group" onClick={onClickDownload}>
              <i className="fa-solid fa-download" />
              {t('一覧出力')}
            </a>
          </li>
        </ul>
      </div>
      <div className="tab-content-wrap wfr in-pgnavi" style={{ paddingBottom: '40px' }}>
        <TaskTable tasks={filteredTasks} taskStatus={currentTaskStatus} />
      </div>
      {/* ダウンロードエラー */}
      {displayError && (
        <OkModal onClickOk={() => setDisplayError(false)}>
          <div>{error?.response?.data.message}</div>
        </OkModal>
      )}
      {/* スピナーモーダルの表示 */}
      {downloading && <SpinnerModal />}
      {/* ダウンロードエラーの表示 */}
      {displayDownloadError && (
        <BlobErrorModal error={downloadError} onClickOk={() => setDisplayDownloadError(false)} />
      )}
    </div>
  );
}

export { TaskList };
